import { Schema as S } from "effect";

import {
	Avatar,
	EntryType,
	HistoryVersion,
	Page,
	Provenance,
	RecordType,
} from "./common";

/** Server `Cadence` (core/records.ts): how often a record should be contacted. */
export const Cadence = S.Literals([
	"weekly",
	"fortnightly",
	"monthly",
	"quarterly",
	"twice-yearly",
	"yearly",
]);
export type Cadence = typeof Cadence.Type;

/** Server `recordDbToApi` (core/utils.ts) over `deserializeRecordDB`: description/lastUpdated are nullable columns. */
export const RecordSummary = S.Struct({
	id: S.String,
	circleId: S.String,
	type: RecordType,
	name: S.String,
	subtitle: S.optionalKey(S.String),
	description: S.optionalKey(S.NullOr(S.String)),
	tags: S.Array(S.String),
	avatars: S.Array(S.String),
	lastUpdated: S.optionalKey(S.NullOr(S.String)),
	timestamp: S.NullOr(S.String),
	attributes: S.optionalKey(S.Record(S.String, S.String)),
	externalId: S.optionalKey(S.Record(S.String, S.String)),
	avatar: S.optionalKey(Avatar),
	/** The creating write; absent from list rows and older responses. */
	provenance: S.optionalKey(S.NullOr(Provenance)),
	lastEdit: S.optionalKey(S.NullOr(Provenance)),
	/** Keep-in-touch cadence; absent when unset or from older responses. */
	cadence: S.optionalKey(Cadence),
	paused: S.optionalKey(S.Boolean),
});
export type RecordSummary = typeof RecordSummary.Type;

export const SearchResult = S.Struct({
	type: S.Literals(["node", "knot", "event"]),
	title: S.String,
	subtitle: S.String,
	id: S.String,
	foundInfo: S.Array(S.Struct({ key: S.String, value: S.String })),
	avatar: S.optionalKey(Avatar),
	avatars: S.optionalKey(S.Array(S.String)),
	nodeType: S.optionalKey(RecordType),
	knotType: S.optionalKey(EntryType),
	/** Server-split highlight segments per matched field (core/searchQuery.ts); absent from older responses. */
	matches: S.optionalKey(
		S.Array(
			S.Struct({
				field: S.String,
				segments: S.Array(S.Struct({ text: S.String, matched: S.Boolean })),
			}),
		),
	),
});
export type SearchResult = typeof SearchResult.Type;

export const ProfileImageExists = S.Struct({
	exists: S.Boolean,
	imageUrl: S.NullOr(S.String),
});

/** Raw `object_history` columns; `data` is the JSON blob (avatars/subtitle), not split out. */
export const RecordVersion = S.Struct({
	type: S.String,
	name: S.NullOr(S.String),
	description: S.NullOr(S.String),
	tags: S.NullOr(S.String),
	attributes: S.NullOr(S.String),
	data: S.optionalKey(S.Unknown),
	avatars: S.optionalKey(S.NullOr(S.String)),
	lastUpdated: S.optionalKey(S.NullOr(S.String)),
});
export const RecordHistoryEntry = HistoryVersion(RecordVersion);
export type RecordHistoryEntry = typeof RecordHistoryEntry.Type;

export const RecordHistory = Page(RecordHistoryEntry);
